// src/components/WritersLoginModal.js
import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../api/api";
import { AuthContext } from "../context/AuthContext"; // Make sure this is imported
import "./WritersLoginModal.css";

const WritersLoginModal = ({ isOpen, onClose }) => {
  const { login } = useContext(AuthContext); // Get login function from AuthContext
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setIdentifier("");
      setPassword("");
      setError("");
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const userData = await loginUser({ email: identifier, password });
      
      // Check if user is a writer
      const userRole = userData?.user?.role || userData?.role || "USER";
      
      if (userRole !== "WRITER") {
        setError("This account is not authorized for writer access.");
        return;
      }
      
      // ✅ IMPORTANT: Call login from AuthContext to update the state
      console.log("Writer login successful, updating AuthContext...");
      login(userData); // This updates the AuthContext and Navbar
      
      onClose(); // Close the modal
      
      // Redirect to writer dashboard
      navigate("/writer-dashboard");
    } catch (err) {
      console.error("Writer login error:", err);
      
      if (err.message && err.message.includes("Invalid")) {
        setError("Invalid email/username or password. Please try again.");
      } else if (err.message && err.message.includes("Network")) {
        setError("Network error. Please check your connection.");
      } else if (err.message && err.message.includes("not authorized")) {
        setError("Only writers can access this area.");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ===== ADD FORGOT PASSWORD HANDLER =====
  const handleForgotPassword = () => {
    console.log("Writer forgot password clicked");
    onClose(); // Close the modal
    
    // Small delay to ensure modal closes before navigation
    setTimeout(() => {
      navigate("/forgot-password");
    }, 100);
  };
  // ======================================

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="writers-modal-overlay" 
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="Writers login modal"
    >
      <div className="writers-modal-content">
        <div className="writers-modal-header">
          <h2 className="writers-modal-title">Writer Login</h2>
          <p className="writers-modal-subtitle">For Writers Only</p>
          <button 
            className="writers-modal-close-btn" 
            onClick={onClose}
            aria-label="Close writers login modal"
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <div className="writers-modal-body">
          {error && (
            <div className="writers-modal-error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="writers-form-group">
              <label htmlFor="writers-login-identifier">Email or Username</label>
              <input
                id="writers-login-identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Enter your email or username"
                required
                disabled={isLoading}
                autoComplete="username"
                aria-describedby={error ? "error-message" : undefined}
              />
            </div>

            <div className="writers-form-group">
              <label htmlFor="writers-login-password">Password</label>
              <input
                id="writers-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isLoading}
                autoComplete="current-password"
                aria-describedby={error ? "error-message" : undefined}
              />
            </div>

            {/* ===== ADD FORGOT PASSWORD LINK ===== */}
            <div className="writers-forgot-password">
              <button
                type="button"
                className="writers-forgot-link"
                onClick={handleForgotPassword}
                disabled={isLoading}
              >
                Forgot your password?
              </button>
            </div>
            {/* =================================== */}

            <button 
              type="submit" 
              className="writers-modal-submit-btn"
              disabled={isLoading || !identifier || !password}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="writers-spinner"></span>
                  Logging in...
                </>
              ) : (
                "Log In"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WritersLoginModal;